import type { StyleRule } from '@archipro-design/aria';
import {
    buttonClassName,
    productInfoSlotClassNames,
    productTileSlotClassNames,
    captionClassName,
    pxArrayToRem,
    pxToRem,
} from '@archipro-design/aria';
import { PADDING_LEFT_RIGHT_SECTION_MOBILE } from '../../common/pagebuilder.style';
import type { ColumnDimensionType } from '@modules/page-builder/types';

const PRODUCT_INFO_MIN_HEIGHT = 110;

export const DetailSingleImageContainer: StyleRule<{
    isTagging?: boolean;
    dimension?: ColumnDimensionType;
    isLinked?: boolean;
    moduleType?: string;
    isPageBuilder?: boolean;
    fullScreen?: boolean | null;
    inViewPort?: boolean;
}> = ({
    isTagging,
    theme,
    dimension,
    isLinked,
    moduleType,
    isPageBuilder,
}) => ({
    position: 'relative',
    width: dimension?.mobileWidth ? pxToRem(dimension.mobileWidth) : '100%',
    height: dimension?.mobileHeight ? pxToRem(dimension.mobileHeight) : '100%',

    [theme.screen.laptop]: {
        width: dimension?.width ? pxToRem(dimension.width) : '100%',
        height: dimension?.height ? pxToRem(dimension.height) : '100%',

        ...(moduleType === 'SingleImageModule' && {
            width: '100% !important',
            aspectRatio: '1440/928',
            height: 'auto',
            transition: 'all 0.5s ease-in-out',
        }),

        // Full page images should have no side margins (take full viewport width)
        ...(moduleType === 'FullPageImageModule' && {
            width: dimension?.width ? pxToRem(dimension.width) : '100%',
            height: dimension?.height ? pxToRem(dimension.height) : '100%',
            marginLeft: '0',
            marginRight: '0',
        }),

        ...(moduleType === 'FeaturedProductModule' && {
            maxWidth: '100% !important',
            width: dimension?.width ? pxToRem(dimension.width) : '100%',
            height: dimension?.height ? pxToRem(dimension.height) : '100%',
        }),

        ...(moduleType === 'MediumSingleImageModule' && {
            width: dimension?.width ? pxToRem(dimension.width * 1.26) : '100%', // Other elements are 1.26x bigger so we're scaling this along
            height: dimension?.height
                ? pxToRem(dimension.height * 1.26)
                : '100%',
        }),

        ...(moduleType === 'ImageParagraphModule' && {
            width: pxToRem(1050),
            // Use natural aspect ratio based on image orientation
            ...(dimension?.isPortrait
                ? { aspectRatio: '1050/1350', height: 'auto' }
                : { aspectRatio: '16/9', height: 'auto' }),
        }),

        // For FeaturedProductModule align to the right on desktop
        ...(moduleType === 'FeaturedProductModule' && {
            marginLeft: 'auto',
            marginRight: 0,
        }),

        ...(moduleType === 'DuoImageModule' &&
            !isPageBuilder && {
                width: '100% !important', // Just make width to 100%, logic for handling each image is in the DuoImageModule.style.ts
            }),
    },

    '&:hover': {
        '& .hover-buttons-image': {
            opacity: isTagging ? 0 : 1,
            visibility: isTagging ? 'hidden' : 'visible',
        },
    },
    cursor: !isTagging && isLinked ? 'pointer' : 'default',
    margin: moduleType === 'ImageParagraphModule' ? '0' : 'auto', // Left align for image paragraph module
});

export const SingleImageWithProductContainer: StyleRule<{
    dimension?: ColumnDimensionType;
    variant?: '01' | '02';
    moduleType?: string;
    isPageBuilder?: boolean;
}> = ({ theme, dimension, variant, moduleType, isPageBuilder }) => ({
    position: 'relative',
    width: dimension?.mobileWidth ? pxToRem(dimension.mobileWidth) : '50%',
    height: 'auto',
    margin: '0 auto',
    maxWidth: '100%',
    minHeight: dimension?.mobileHeight
        ? pxToRem(dimension.mobileHeight + PRODUCT_INFO_MIN_HEIGHT)
        : 0,

    [`& .${productInfoSlotClassNames.container}`]: {
        padding:
            variant === '02' ? pxArrayToRem([0, 18]) : pxArrayToRem([0, 12]),
    },

    [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
        fontSize: pxToRem(12),
    },

    [theme.screen.laptop]: {
        maxWidth: '100%',
        width: (() => {
            // No dimension available or in page builder - use full width
            if (!dimension?.width || isPageBuilder) {
                return '100%';
            }

            // Featured product modules get a 1.26x multiplier
            if (moduleType === 'FeaturedProductModule') {
                return pxToRem(dimension.width * 1.26);
            }

            // Standard modules use exact dimension width
            return pxToRem(dimension.width);
        })(),
        minHeight: dimension?.height
            ? pxToRem(dimension.height * 1.26 + PRODUCT_INFO_MIN_HEIGHT)
            : 0,
        [`& .${productInfoSlotClassNames.container}`]: {
            padding: 0,
        },
        // Ensure product CTA button has no left margin so it aligns with image edges
        [`& .${buttonClassName}`]: {
            ...(moduleType === 'FeaturedProductModule' && {
                marginLeft: 0,
            }),
        },

        [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
            fontSize: pxToRem(16),
        },
    },
});

export const GalleryProductContainer: StyleRule<{
    dimension?: ColumnDimensionType;
    variant?: '01' | '02';
}> = ({ theme, dimension, variant }) => ({
    position: 'relative',
    width: '100%', // Take full width of gallery container
    height: 'auto',
    minHeight: dimension?.mobileHeight
        ? pxToRem(dimension.mobileHeight + PRODUCT_INFO_MIN_HEIGHT)
        : 0,

    [`& .${productInfoSlotClassNames.container}`]: {
        padding:
            variant === '02' ? pxArrayToRem([0, 18]) : pxArrayToRem([0, 12]),
    },

    [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
        fontSize: pxToRem(12),
    },

    [theme.screen.laptop]: {
        width: '100%', // Always take full width
        minHeight: dimension?.height
            ? pxToRem(dimension.height + PRODUCT_INFO_MIN_HEIGHT)
            : 0,
        [`& .${productInfoSlotClassNames.container}`]: {
            padding: 0,
        },

        [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
            fontSize: pxToRem(16),
        },
    },
});

export const ImageCaptionContainer: StyleRule<{
    dimension?: ColumnDimensionType;
    moduleType?: string;
    isProject?: boolean;
    isPageBuilder?: boolean;
}> = ({ theme, dimension, moduleType, isProject, isPageBuilder }) => ({
    flexGrow: 1,
    position: 'relative',
    width: '100%',
    maxWidth: dimension?.mobileWidth ? pxToRem(dimension.mobileWidth) : 'unset',
    height: 'auto',
    margin: '0',

    // Target the caption element specifically to ensure styles apply
    [`& .${captionClassName}`]: {
        fontSize: pxToRem(22),
        lineHeight: '120%',
        letterSpacing: '0.015em',
        fontWeight: 450,
        color: '#161616',
        fontFamily: 'neue-haas-grotesk-display',
    },

    [theme.screen.laptop]: {
        maxWidth: '100%',
        ...((moduleType === 'SingleImageModule' ||
            moduleType === 'FullPageGalleryModule') &&
            isProject && {
                width: isPageBuilder ? '100%' : pxToRem(1300),
                maxWidth: isPageBuilder
                    ? dimension?.width
                        ? pxToRem(dimension.width)
                        : '100%'
                    : 'unset',
            }),
        ...(moduleType === 'FullPageImageModule' && {
            width: '100%',
            maxWidth: pxToRem(1820),

            marginLeft: 'auto',
            marginRight: 'auto',

            [`& .${captionClassName}`]: {
                fontSize: `${pxToRem(20)} !important`,
            },
        }),
        ...(moduleType === 'MediumSingleImageModule' && {
            width: dimension?.width ? pxToRem(dimension.width * 1.26) : '100%',
            maxWidth: dimension?.width
                ? pxToRem(dimension.width * 1.26)
                : '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
        }),
        ...(moduleType === 'ImageParagraphModule' && {
            maxWidth: '100%',
            margin: '0 !important',
            width: pxToRem(1050), // Match the image width for image paragraph module
        }),
    },
});

export const MobileCaptionContainer: StyleRule = ({ theme }) => ({
    padding: pxArrayToRem([0, PADDING_LEFT_RIGHT_SECTION_MOBILE]),
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.screen.tablet]: {
        padding: 0,
    },
});

export const DetailSingleImageLinkedIcon: StyleRule = () => ({
    zIndex: 1,
    position: 'absolute',
    bottom: pxToRem(12),
    right: pxToRem(12),

    [`& .${buttonClassName}`]: {
        borderRadius: '50%',
        padding: pxToRem(8),
        width: pxToRem(48),
        height: pxToRem(48),
    },
});
